import { MERCHANT_TERMS } from '@field-support/shared';

/**
 * Bulk import copy (Arabic). `import` is carried verbatim from the previous
 * app; `import.preview*`, `import.check*`, `import.issueLine` and
 * `import.tooManyRows` are new: the previous app only counted the rows with
 * client-side problems, this one lists them and translates each problem.
 */
export const importAr = {
  import: {
    title: `استيراد جماعي ${MERCHANT_TERMS.ar.forMany} والفروع`,
    description: `ينشئ أو يحدّث ${MERCHANT_TERMS.ar.many} والفروع من ملف CSV أو Excel.`,
    help: 'نزّل القالب، واملا صف لكل فرع، وبعدين اختار الملف هنا. القالب فيه صفحة «التعليمات» بتشرح كل عمود. العناوين بالعربي أو الإنجليزي الاتنين مقبولين.',
    requiredColumns: 'أعمدة إجبارية: {{columns}}.',
    optionalColumns: 'اختيارية: {{columns}}.',
    fileTypes: '<xlsx>Excel (.xlsx)</xlsx> أو <csv>CSV (.csv)</csv>',
    downloadTemplate: 'تنزيل القالب',
    unsupportedFile: 'نوع الملف ده مش مدعوم. استخدم القالب (.xlsx) أو احفظ الشيت كـ .csv.',
    unreadableFile: 'تعذر قراءة الملف: {{detail}}',
    template: {
      dataSheet: 'الاستيراد',
      instructionsSheet: 'التعليمات',
      column: 'العمود',
      required: 'إجباري',
      format: 'الشكل المقبول',
      example: 'مثال',
      failed: 'تعذر إنشاء القالب.',
    },
    statusIdle: 'لم يتم تحميل ملف',
    statusParsed: 'صف مقروء من',
    statusParsed_one: 'صف مقروء من',
    statusParsed_two: 'صفين مقروءين من',
    statusParsed_few: 'صفوف مقروءة من',
    chooseFile: 'اختر ملف',
    parsed: 'تمت قراءة {{count}} صف من {{file}}',
    parsed_one: 'تمت قراءة صف واحد من {{file}}',
    parsed_two: 'تمت قراءة صفين من {{file}}',
    parsed_few: 'تمت قراءة {{count}} صفوف من {{file}}',
    missingColumns: 'أعمدة إجبارية ناقصة: {{columns}}. نزّل القالب عشان تاخد العناوين المظبوطة.',
    clientErrors: '{{count}} صف به أخطاء وسيظهر في تقرير الخادم',
    clientErrors_one: 'صف واحد به أخطاء وسيظهر في تقرير الخادم',
    clientErrors_two: 'صفان بهما أخطاء وسيظهران في تقرير الخادم',
    clientErrors_few: '{{count}} صفوف بها أخطاء وستظهر في تقرير الخادم',
    updateExisting: 'تحديث الفروع الموجودة',
    dryRun: 'معاينة (بدون حفظ)',
    import: 'استيراد',
    running: 'جارٍ إرسال الصفوف إلى الخادم',
    previewTitle: 'معاينة الاستيراد (بدون حفظ)',
    finished: 'انتهى الاستيراد',
    summary: `${MERCHANT_TERMS.ar.manyIndefinite} جدد: {{createdBrands}} · فروع جديدة: {{createdBranches}} · تحديث: {{updatedBranches}} · تخطي: {{skipped}} · أخطاء: {{errors}}`,
    rowsWithoutError: 'صف بدون أخطاء',
    row: 'الصف',
    outcome: 'النتيجة',
    message: 'التفاصيل',
    brandCreated: `${MERCHANT_TERMS.ar.one} جديد`,
    onlyProblems: 'الأخطاء والصفوف المتخطاة فقط',
    dryRunDone: 'معاينة فقط — لم يتم حفظ شيء.',
    previewRows: 'صفوف الملف',
    previewHint: 'المراجعة هنا من المتصفح قبل الإرسال. المعاينة بتسأل الخادم ومش بتحفظ حاجة.',
    previewShown: 'معروض {{shown}} من {{total}} صف. كل الصفوف اللي فيها مشكلة معروضة.',
    check: 'المراجعة',
    checkOk: 'سليم',
    checkFailed: 'فيه مشكلة',
    issueLine: '{{column}}: {{problem}}',
    tooManyRows: 'الملف فيه {{count}} صف، والحد الأقصى {{max}} صف في الاستيراد الواحد. قسّم الملف.',
    /** Why a report row is an error or skipped (BULK_IMPORT_ROW_REASONS). */
    reason: {
      INVALID_ROW: 'الصف مش صحيح: {{issues}}', // new — needs native review
      GEOFENCE_NOT_PERMITTED: 'نطاق الفرع محتاج صلاحية «تحديد نطاق الفرع».', // new — needs native review
      DUPLICATE_ROW: `نفس ${MERCHANT_TERMS.ar.the} والفرع زي الصف {{row}}.`, // new — needs native review
      DUPLICATE_BRANCH_CODE_IN_FILE: 'كود الفرع {{code}} مستخدم كمان في الصف {{row}}.', // new — needs native review
      MERCHANT_DELETED: `${MERCHANT_TERMS.ar.the} {{merchant}} محذوف.`, // new — needs native review
      MERCHANT_CODE_CONFLICT_IN_FILE: `كود ${MERCHANT_TERMS.ar.the} {{code}} مختلف عن {{otherCode}} اللي اتكتب لنفس ${MERCHANT_TERMS.ar.the} في الصف {{row}}.`, // new — needs native review
      MERCHANT_CODE_SHARED_IN_FILE: `كود ${MERCHANT_TERMS.ar.the} {{code}} مستخدم لأكتر من ${MERCHANT_TERMS.ar.one} في الملف.`, // new — needs native review
      MERCHANT_CODE_TAKEN: `كود ${MERCHANT_TERMS.ar.the} {{code}} بتاع ${MERCHANT_TERMS.ar.one} تاني ({{merchant}}).`, // new — needs native review
      MERCHANT_HAS_OTHER_CODE: `${MERCHANT_TERMS.ar.the} ليه كود تاني بالفعل: {{code}}.`, // new — needs native review
      AREA_AMBIGUOUS: 'المنطقة {{area}} موجودة في أكتر من قطاع ({{zones}})؛ اكتب القطاع.', // new — needs native review
      BRANCH_DELETED: 'الفرع {{branch}} محذوف.', // new — needs native review
      BRANCH_CODE_TAKEN: 'كود الفرع {{code}} بتاع فرع تاني.', // new — needs native review
      CONCURRENT_CHANGE: 'حد تاني غيّر البيانات في نفس الوقت؛ شغّل الاستيراد تاني.', // new — needs native review
      BRANCH_EXISTS: 'الفرع موجود بالفعل (تحديث الفروع الموجودة مقفول).', // new — needs native review
      NO_CHANGES: 'مفيش تغيير: الصف مطابق للفرع.', // new — needs native review
    },
  },
};
